'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'

export default function CmdKPalette() {
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const router = useRouter()

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setIsOpen(prev => !prev)
      } else if (e.key === 'Escape' && isOpen) {
        setIsOpen(false)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  if (!isOpen) return null

  const actions = [
    { title: 'Script Vault Command', category: 'Navigation', href: '/vault', icon: '⚡' },
    { title: 'Sandbox Playground', category: 'Navigation', href: '/vault/playground', icon: '🧪' },
    { title: 'System Documentation Manual', category: 'Navigation', href: '/docs', icon: '📖' },
    { title: 'Store & Cosmetics', category: 'Navigation', href: '/store', icon: '🛍️' },
    { title: 'Publish Script Payload', category: 'Action', href: '/vault/new', icon: '➕' },
    { title: 'Add Documentation Article', category: 'Action', href: '/docs/new', icon: '📝' },
    { title: 'Platform Settings', category: 'Action', href: '/settings', icon: '⚙️' },
  ]

  const filteredActions = actions.filter(a => 
    a.title.toLowerCase().includes(query.toLowerCase()) || 
    a.category.toLowerCase().includes(query.toLowerCase())
  )

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex items-start justify-center pt-20 px-4 font-mono">
      <div className="bg-[#070b14] border border-indigo-500/40 rounded-2xl w-full max-w-xl shadow-[0_0_50px_rgba(99,102,241,0.2)] overflow-hidden">
        
        {/* Search Input Bar */}
        <div className="flex items-center px-4 border-b border-slate-800">
          <span className="text-slate-500 mr-3">🔍</span>
          <input
            autoFocus
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type a command or search pages (Cmd+K)..."
            className="w-full bg-transparent py-4 text-xs sm:text-sm text-slate-100 focus:outline-none"
          />
          <button 
            onClick={() => setIsOpen(false)}
            className="text-[10px] bg-slate-900 border border-slate-800 px-2 py-1 rounded text-slate-400 hover:text-white"
          >
            ESC
          </button>
        </div>

        {/* Results List */}
        <div className="p-2 max-h-80 overflow-y-auto space-y-1">
          {filteredActions.length === 0 ? (
            <div className="p-6 text-center text-xs text-slate-500">
              No matching commands found.
            </div>
          ) : (
            filteredActions.map((action, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setIsOpen(false)
                  router.push(action.href)
                }}
                className="w-full text-left flex items-center justify-between p-3 rounded-xl hover:bg-indigo-600/20 hover:border-indigo-500/40 border border-transparent transition-all group cursor-pointer text-xs"
              >
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-sm shadow-inner group-hover:border-indigo-500/40">
                    {action.icon}
                  </span>
                  <div>
                    <div className="font-bold text-white group-hover:text-indigo-300 transition-colors">{action.title}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-widest">{action.category}</div>
                  </div>
                </div>
                <span className="text-[10px] text-slate-600 group-hover:text-indigo-400 font-bold">&rarr;</span>
              </button>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="bg-[#020408] border-t border-slate-800/80 px-4 py-2.5 flex items-center justify-between text-[10px] text-slate-500">
          <span>Use click or tap to execute command</span>
          <span className="text-indigo-400 font-bold">DevYield Command Registry</span>
        </div>

      </div>
    </div>
  )
}